import { cn } from '@/lib/cn';

/**
 * A labelled on/off switch (§ Instellingen › Privacy, PLAN-FASE2.md WP-08):
 * no `Toggle`/`Switch` exists yet elsewhere in `app/components/ui`, so this
 * is kept local to `features/settings` rather than promoted — promote it if
 * a later WP needs the same control.
 */
export function Toggle({
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      className={cn(
        'flex items-center justify-between gap-4 py-2',
        disabled ? 'opacity-50' : 'cursor-pointer',
      )}
    >
      <span className="min-w-0">
        <span className="text-ink block text-sm font-medium">{label}</span>
        {hint && <span className="text-accent block text-[13px]">{hint}</span>}
      </span>
      <span
        role="switch"
        aria-checked={checked}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && onChange(!checked)}
        onKeyDown={(event) => {
          if (disabled) return;
          if (event.key === ' ' || event.key === 'Enter') {
            event.preventDefault();
            onChange(!checked);
          }
        }}
        className={cn(
          'rounded-pill relative h-6 w-11 shrink-0 border transition-colors motion-reduce:transition-none',
          'focus-visible:ring-accent focus-visible:ring-offset-paper focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
          checked ? 'bg-mint border-mint' : 'bg-paper border-line',
        )}
      >
        <span
          className={cn(
            'rounded-pill bg-paper border-line absolute top-0.5 left-0.5 size-4 border transition-transform motion-reduce:transition-none',
            checked && 'translate-x-5',
          )}
        />
      </span>
    </label>
  );
}
